import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { DataTable, type Column } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowRight, Banknote, CheckSquare, ClipboardList, Play, CheckCircle, Truck, Calendar, User, ExternalLink, Trash2, Plus, ClipboardCheck } from 'lucide-react';
import { Manifest, MANIFEST_TYPE_LABELS, MANIFEST_STATUS_LABELS } from '@/types/manifest.types';
import { Trip, TripType, TRIP_TYPE_LABELS } from '@/types/trip.types';
import { ROUTES } from '@/lib/constants';
import { formatDate } from '@/lib/formatters';

interface ManifestWithTrips extends Manifest {
  trips?: Trip[];
  notes?: string;
}

interface Driver {
  id: string;
  user?: { id: string; name: string };
  name?: string;
}

interface AvailableTrip {
  id: string;
  trip_type: TripType;
  status: string;
  shipment?: { tracking_number?: string };
}

export default function ManifestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, patch, post, delete: del } = useApi();
  const [manifest, setManifest] = useState<ManifestWithTrips | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [showCodDialog, setShowCodDialog] = useState(false);
  const [codAmount, setCodAmount] = useState('');
  const [codNotes, setCodNotes] = useState('');
  const [codSubmitting, setCodSubmitting] = useState(false);

  // Draft-mode state
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [draftNotes, setDraftNotes] = useState('');
  const [showAddTripDialog, setShowAddTripDialog] = useState(false);
  const [availableTrips, setAvailableTrips] = useState<AvailableTrip[]>([]);
  const [loadingAvailableTrips, setLoadingAvailableTrips] = useState(false);
  const [addingTripId, setAddingTripId] = useState<string | null>(null);
  const [confirmingDraft, setConfirmingDraft] = useState(false);

  const fetchManifest = useCallback(async () => {
    if (!id) return;
    try {
      const result: any = await get(`/api/v1/dashboard/manifests/${id}`);
      const data = result?.data ?? result;
      setManifest(data);
      if (data?.notes) {
        setDraftNotes(data.notes);
      }
    } catch (error) {
      toast.error('فشل في جلب تفاصيل البيان');
    } finally {
      setHasFetched(true);
    }
  }, [id]);

  useEffect(() => {
    fetchManifest();
  }, [fetchManifest]);

  // Load drivers when manifest is draft
  useEffect(() => {
    if (!manifest || manifest.status !== 'draft' || !manifest.carrier_id) return;
    get(`/api/v1/drivers?carrier_id=${manifest.carrier_id}`)
      .then((result: any) => {
        const list = result?.data ?? result?.list ?? result ?? [];
        setDrivers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        // silently fail — drivers list is best-effort
      });
  }, [manifest?.status, manifest?.carrier_id]);

  const handleStart = async () => {
    if (!id) return;
    try {
      await patch(`/api/v1/dashboard/manifests/${id}/start`, {});
      toast.success('تم بدء البيان بنجاح');
      fetchManifest();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في بدء البيان';
      toast.error(message);
    }
  };

  const handleComplete = async () => {
    if (!id) return;
    try {
      await patch(`/api/v1/dashboard/manifests/${id}/complete`, {});
      toast.success('تم إتمام البيان بنجاح');
      fetchManifest();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في إتمام البيان';
      toast.error(message);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await patch(`/api/v1/dashboard/manifests/${id}/accept`, {});
      toast.success('تم تسليم البيان للسائق بنجاح');
      fetchManifest();
    } catch {
      // useApi surfaces toast
    } finally {
      setAccepting(false);
    }
  };

  const handleCodHandover = async () => {
    if (!codAmount || Number(codAmount) < 0) return;
    setCodSubmitting(true);
    try {
      await post(`/api/v1/dashboard/manifests/${id}/cod-handover`, {
        amount_collected: Number(codAmount),
        notes: codNotes.trim() || undefined,
      });
      toast.success('تم تسجيل تسليم المبالغ بنجاح');
      setShowCodDialog(false);
      setCodAmount('');
      setCodNotes('');
      fetchManifest();
    } catch {
      // useApi surfaces toast
    } finally {
      setCodSubmitting(false);
    }
  };

  // Draft-mode handlers
  const handleDriverChange = async (driverId: string) => {
    if (!id) return;
    try {
      await patch(`/api/v1/dashboard/manifests/${id}`, { driver_id: driverId });
      toast.success('تم تحديث السائق');
      fetchManifest();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في تحديث السائق';
      toast.error(message);
    }
  };

  const handleDateChange = async (date: string) => {
    if (!id || !date) return;
    try {
      await patch(`/api/v1/dashboard/manifests/${id}`, { date });
      toast.success('تم تحديث التاريخ');
      fetchManifest();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في تحديث التاريخ';
      toast.error(message);
    }
  };

  const handleNotesBlur = async () => {
    if (!id) return;
    try {
      await patch(`/api/v1/dashboard/manifests/${id}`, { notes: draftNotes.trim() });
      toast.success('تم حفظ الملاحظات');
      fetchManifest();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في حفظ الملاحظات';
      toast.error(message);
    }
  };

  const handleConfirmDraft = async () => {
    if (!id) return;
    setConfirmingDraft(true);
    try {
      await patch(`/api/v1/dashboard/manifests/${id}/confirm`, {});
      toast.success('تم تأكيد البيان بنجاح');
      fetchManifest();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في تأكيد البيان';
      toast.error(message);
    } finally {
      setConfirmingDraft(false);
    }
  };

  const handleRemoveTrip = async (tripId: string) => {
    if (!id) return;
    try {
      await del(`/api/v1/dashboard/manifests/${id}/trips/${tripId}`);
      toast.success('تم حذف الرحلة');
      fetchManifest();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في حذف الرحلة';
      toast.error(message);
    }
  };

  const handleOpenAddTripDialog = async () => {
    if (!id) return;
    setShowAddTripDialog(true);
    setLoadingAvailableTrips(true);
    try {
      const result: any = await get(`/api/v1/dashboard/manifests/${id}/available-trips`);
      const list = result?.data ?? result?.list ?? result ?? [];
      setAvailableTrips(Array.isArray(list) ? list : []);
    } catch {
      toast.error('فشل في جلب الرحلات المتاحة');
      setAvailableTrips([]);
    } finally {
      setLoadingAvailableTrips(false);
    }
  };

  const handleAddTrip = async (tripId: string) => {
    if (!id) return;
    setAddingTripId(tripId);
    try {
      await post(`/api/v1/dashboard/manifests/${id}/trips/${tripId}`, {});
      toast.success('تمت إضافة الرحلة');
      setShowAddTripDialog(false);
      setAvailableTrips([]);
      fetchManifest();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في إضافة الرحلة';
      toast.error(message);
    } finally {
      setAddingTripId(null);
    }
  };

  const getTripStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'completed': return 'default';
      case 'in_transit': return 'outline';
      case 'failed': return 'destructive';
      default: return 'secondary';
    }
  };

  const getTripStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      planned: 'مخطط',
      pending: 'قيد الانتظار',
      assigned: 'معين',
      picked_up: 'تم الاستلام',
      in_transit: 'قيد التنفيذ',
      arrived: 'وصل',
      completed: 'مكتمل',
      failed: 'فشل',
      cancelled: 'ملغى',
    };
    return labels[status] || status;
  };

  if (!hasFetched) {
    return (
      <PageContainer>
        <LoadingSkeleton type="details" />
      </PageContainer>
    );
  }

  if (!manifest) return null;

  const trips = manifest.trips || [];
  const isDraft = manifest.status === 'draft';
  const canStart = manifest.status === 'accepted';
  const canAccept = manifest.status === 'assigned';
  const canComplete = manifest.status === 'in_progress';
  const canCodHandover =
    manifest.manifest_type === 'delivery_run' &&
    (manifest.status === 'completed' || manifest.status === 'partially_completed') &&
    Number(manifest.total_cod_collected ?? 0) === 0;

  const tripColumns: Column<Trip>[] = [
    {
      header: '#',
      accessor: (row) => (
        <span className="text-muted-foreground text-sm">{row.sequence}</span>
      ),
    },
    {
      header: 'رقم التتبع',
      accessor: (row) => {
        const tracking = (row as any).shipment?.tracking_number;
        const shipmentId = (row as any).shipment?.id;
        if (!tracking) return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <button
            className="flex items-center gap-1 font-mono text-sm text-primary hover:underline"
            onClick={(e) => { e.stopPropagation(); navigate(ROUTES.SHIPMENT_DETAILS(shipmentId)); }}
          >
            {tracking}
            <ExternalLink className="h-3 w-3" />
          </button>
        );
      },
    },
    {
      header: 'نوع الرحلة',
      accessor: (row) => (
        <span className="text-sm">{TRIP_TYPE_LABELS[row.trip_type] || row.trip_type}</span>
      ),
    },
    {
      header: 'الحالة',
      accessor: (row) => (
        <Badge variant={getTripStatusVariant(row.status)}>
          {getTripStatusLabel(row.status)}
        </Badge>
      ),
    },
    ...(isDraft
      ? [
          {
            header: '',
            accessor: (row: Trip) => (
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={(e) => { e.stopPropagation(); handleRemoveTrip(row.id); }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            ),
          } satisfies Column<Trip>,
        ]
      : []),
  ];

  const driverDisplayName = (d: Driver) =>
    d.user?.name ?? d.name ?? d.id;

  return (
    <PageContainer>
      <div className="space-y-6" dir="rtl">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(ROUTES.MANIFESTS)}>
            <ArrowRight className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold">
                {MANIFEST_TYPE_LABELS[manifest.manifest_type] || manifest.manifest_type}
                <span className="font-mono text-base font-normal text-muted-foreground ms-2">({manifest.manifest_number})</span>
              </h1>
              <Badge variant={manifest.status === 'completed' ? 'default' : 'secondary'}>
                {MANIFEST_STATUS_LABELS[manifest.status] || manifest.status}
              </Badge>
            </div>
          </div>
          <div className="flex gap-2">
            {isDraft && (
              <Button
                onClick={handleConfirmDraft}
                disabled={confirmingDraft}
                className="gap-2"
              >
                <ClipboardCheck className="h-4 w-4" />
                {confirmingDraft ? 'جارٍ التأكيد...' : 'تأكيد البيان'}
              </Button>
            )}
            {canAccept && (
              <Button
                onClick={handleAccept}
                disabled={accepting}
                variant="outline"
                className="gap-2"
              >
                <CheckSquare className="h-4 w-4" />
                تسليم للسائق
              </Button>
            )}
            {canStart && (
              <Button onClick={handleStart} className="gap-2 bg-green-600 hover:bg-green-700">
                <Play className="h-4 w-4" />
                بدء البيان
              </Button>
            )}
            {canComplete && (
              <Button onClick={handleComplete} className="gap-2">
                <CheckCircle className="h-4 w-4" />
                إتمام البيان
              </Button>
            )}
            {canCodHandover && (
              <Button
                onClick={() => setShowCodDialog(true)}
                variant="outline"
                className="gap-2"
              >
                <Banknote className="h-4 w-4" />
                تسليم المبالغ
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Driver card */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-muted-foreground">السائق</p>
                  {isDraft ? (
                    <Select
                      value={manifest.driver_id ?? ''}
                      onValueChange={handleDriverChange}
                    >
                      <SelectTrigger className="mt-1 h-8 text-sm">
                        <SelectValue placeholder="اختر سائقاً" />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {driverDisplayName(d)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-base font-semibold truncate">
                      {(manifest as any).driver?.user?.name || 'غير معين'}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Node card */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <ClipboardList className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">المنطلق</p>
                  <p className="text-base font-semibold">{manifest.node?.name || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trips count card */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Truck className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">الرحلات</p>
                  <p className="text-xl font-bold">
                    {manifest.completed_trips}/{manifest.total_trips}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Date card */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-muted-foreground">التاريخ</p>
                  {isDraft ? (
                    <Input
                      type="date"
                      className="mt-1 h-8 text-sm"
                      defaultValue={manifest.date ? manifest.date.slice(0, 10) : ''}
                      onBlur={(e) => handleDateChange(e.target.value)}
                    />
                  ) : (
                    <p className="text-base font-semibold">{formatDate(manifest.date)}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes card — draft only, full width */}
          {isDraft && (
            <Card className="col-span-2 md:col-span-4">
              <CardContent className="pt-4 pb-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">ملاحظات</p>
                  <Textarea
                    rows={3}
                    placeholder="أضف ملاحظات على البيان..."
                    value={draftNotes}
                    onChange={(e) => setDraftNotes(e.target.value)}
                    onBlur={handleNotesBlur}
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Trips Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>الرحلات ({trips.length})</CardTitle>
            {isDraft && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={handleOpenAddTripDialog}
              >
                <Plus className="h-4 w-4" />
                إضافة رحلة
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {trips.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">لا توجد رحلات في هذا البيان</p>
            ) : (
              <DataTable
                data={trips}
                columns={tripColumns}
                emptyMessage="لا توجد رحلات"
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* COD Handover Dialog */}
      <Dialog open={showCodDialog} onOpenChange={setShowCodDialog}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>تسليم مبالغ الدفع عند الاستلام</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cod_amount">المبلغ المحصّل</Label>
              <Input
                id="cod_amount"
                type="number"
                min="0"
                step="0.01"
                dir="ltr"
                value={codAmount}
                onChange={(e) => setCodAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cod_notes">ملاحظات (اختياري)</Label>
              <Textarea
                id="cod_notes"
                rows={2}
                value={codNotes}
                onChange={(e) => setCodNotes(e.target.value)}
                placeholder="أي ملاحظات إضافية..."
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCodDialog(false)}>
              إلغاء
            </Button>
            <Button
              onClick={handleCodHandover}
              disabled={codSubmitting || !codAmount || Number(codAmount) < 0}
            >
              {codSubmitting ? 'جارٍ الحفظ...' : 'تأكيد التسليم'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Trip Dialog */}
      <Dialog open={showAddTripDialog} onOpenChange={(open) => { setShowAddTripDialog(open); if (!open) setAvailableTrips([]); }}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle>إضافة رحلة إلى البيان</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {loadingAvailableTrips && (
              <p className="text-center text-muted-foreground py-6">جارٍ التحميل...</p>
            )}
            {!loadingAvailableTrips && availableTrips.length === 0 && (
              <p className="text-center text-muted-foreground py-6">لا توجد رحلات متاحة للإضافة</p>
            )}
            {!loadingAvailableTrips && availableTrips.map((trip) => (
              <div
                key={trip.id}
                className="flex items-center justify-between rounded-md border px-4 py-3 gap-3"
              >
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="font-mono text-sm font-medium truncate">
                    {trip.shipment?.tracking_number || trip.id}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {TRIP_TYPE_LABELS[trip.trip_type] || trip.trip_type}
                    </span>
                    <Badge variant="secondary" className="text-xs py-0">
                      {getTripStatusLabel(trip.status)}
                    </Badge>
                  </div>
                </div>
                <Button
                  size="sm"
                  disabled={addingTripId === trip.id}
                  onClick={() => handleAddTrip(trip.id)}
                >
                  {addingTripId === trip.id ? '...' : 'إضافة'}
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddTripDialog(false); setAvailableTrips([]); }}>
              إغلاق
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
